import {IconCalendar} from "./svg-icons/IconCalendar.tsx";
import {useEffect, useRef, useState} from 'preact/hooks'
import {SlideToggle} from "./SlideToggle.tsx";
import {DateRangePicker} from "./DateRangePicker.tsx";
import {YearMonthPicker} from "./YearMonthPicker.tsx";
import {FilterOption, SearchFilters, UpdateFilterItem} from "../types/travel";
import {IconCloseBig} from "./svg-icons/IconCloseBig.tsx";

interface TravelDateSelectorProps {
    placeholder?: string
    label?: string
    cssClasses?: string
    options?: FilterOption[]
    selectedValues?: string[]
    onSelectionChange?: (category: keyof SearchFilters, value: string[]) => void
    onDateSelection?: (updateFilterItems: UpdateFilterItem[]) => void
    gridViewState: boolean
    setGridViewState: (value: boolean) => void
    initialStart?: string
    initialEnd?: string
}

export function TravelDateSelector({
                                       placeholder = "Wann?",
                                       label = "REISEZEITRAUM",
                                       cssClasses = '',
                                       options = [],
                                       selectedValues = [],
                                       onSelectionChange,
                                       onDateSelection,
                                       gridViewState,
                                       setGridViewState,
                                       initialStart,
                                       initialEnd
                                } : TravelDateSelectorProps) {

    // Helper function to parse YYYYMMDD date string to Date object
    const parseDateString = (dateStr: string | undefined): Date | null => {
        if (!dateStr || dateStr.length !== 8) return null
        const year = parseInt(dateStr.substring(0, 4))
        const month = parseInt(dateStr.substring(4, 6)) - 1 // 0-based month
        const day = parseInt(dateStr.substring(6, 8))
        return new Date(year, month, day, 12, 0, 0, 0) // Set to noon to avoid timezone issues
    }

    // German month names for formatting display
    const monthNames = [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ]

    // Format selectedValues to display like YearMonthPicker labels
    const formatSelectedValues = (values: string[]): string => {
        // Check if "later" is selected
        if (values.includes('later')) {
            return 'Alle späteren Termine'
        }

        if (values.includes('flexible')) {
            return 'Flexibel'
        }

        const filteredAndSorted = values
            .filter(value => value.length === 6) // Only valid YYYYMM format
            .sort((a, b) => a.localeCompare(b)) // Sort chronologically (YYYYMM strings sort correctly)
            .map(value => {
                const year = parseInt(value.substring(0, 4))
                const month = parseInt(value.substring(4, 6)) - 1 // 0-based for array index
                return `${monthNames[month]} ${year}`
            })

        if (filteredAndSorted.length === 0) {
            return ''
        }

        if (filteredAndSorted.length === 1) {
            return filteredAndSorted[0]
        }

        // Multiple values: show first value + ", ..."
        return filteredAndSorted.join(', ')
    }

    // Format date range for display in DD.MM.YYYY - DD.MM.YYYY format
    const formatDateRange = (range: { start: Date | null; end: Date | null }): string => {
        if (!range.start) return ''

        const formatDate = (date: Date): string => {
            const day = date.getDate().toString().padStart(2, '0')
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const year = date.getFullYear()
            return `${day}.${month}.${year}`
        }

        if (!range.end) {
            return `${formatDate(range.start)} -`
        }

        return `${formatDate(range.start)} - ${formatDate(range.end)}`
    }

    const selectedOptions = formatSelectedValues(selectedValues);
    const [isOpen, setIsOpen] = useState(false)
    const [selectedDateRange, setSelectedDateRange] = useState<{ start: Date | null; end: Date | null }>({
        start: parseDateString(initialStart),
        end: parseDateString(initialEnd)
    })
    const inputRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [isAlleSpaeterenTermineSelected, setIsAlleSpaeterenTermineSelected] = useState(false)
    const [isFlexibelSelected, setIsFlexibelSelected] = useState(false)
    const [showPlaceholder, setShowPlaceholder] = useState(true)

    const handleClickContainer = () => {
        setIsOpen(true)
        inputRef.current?.focus()
        setShowPlaceholder(false)
    }

    function handleInputBlur(selectedOptions:string) {
        if(selectedOptions.length === 0) {
            setShowPlaceholder(true)
        }
    }
    const handleDateRangeSelect = (range: { start: Date | null; end: Date | null }) => {
        setIsAlleSpaeterenTermineSelected(false)
        setIsFlexibelSelected(false)
        setSelectedDateRange(range)
        if(onDateSelection) {
            onDateSelection([
                {category: 'yearmonth', values: []},
                {category: 'start', values: range.start ? range.start.toISOString().slice(0, 10).replace(/-/g, '') : ''},
                {category: 'end', values: range.end ? range.end.toISOString().slice(0, 10).replace(/-/g, '') : ''}
            ])
        }
    }

    const handleYearMonthSelectionChange = (values: string[]) => {
        if (!onSelectionChange) return
        if(onDateSelection) {
            onDateSelection([
                {category: 'yearmonth', values: values},
                {category: 'start', values: ''},
                {category: 'end', values: ''}
            ])
            setSelectedDateRange({start: null, end: null})
        }
        // onSelectionChange('yearmonth', values)
    }

    const resetDates = () => {
        if(onDateSelection){
            setIsAlleSpaeterenTermineSelected(false)
            setIsFlexibelSelected(false)
            setSelectedDateRange({start: null, end: null})
            onDateSelection([
                {category: 'yearmonth', values: []},
                {category: 'start', values: ''},
                {category: 'end', values: ''}
            ])
            inputRef.current?.blur()
            setIsOpen(false)
        }
    }

    useEffect(() => {
        if(isFlexibelSelected || isAlleSpaeterenTermineSelected) {
            setIsOpen(false)
        }
    }, [isFlexibelSelected, isAlleSpaeterenTermineSelected]);

    // Set gridViewState to false if there's an initial date range
    useEffect(() => {
        if (selectedDateRange.start && selectedDateRange.end) {
            setGridViewState(false)
        }
    }, []); // Run only on mount

    // Close dropdown when clicking outside - Shadow DOM compatible
    useEffect(() => {
        const handleClickOutside = (event: Event) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        // For Shadow DOM compatibility, listen on both document and shadow root
        const shadowRoot = containerRef.current?.getRootNode() as ShadowRoot | Document

        if (shadowRoot && shadowRoot !== document) {
            // We're in a Shadow DOM
            shadowRoot.addEventListener('mousedown', handleClickOutside)
            return () => shadowRoot.removeEventListener('mousedown', handleClickOutside)
        } else {
            // Regular DOM
            document.addEventListener('mousedown', handleClickOutside)
            return () => document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    return (
        <div className={`travel-date-selector relative ${cssClasses}`} ref={containerRef}>
            <div
                className="travel-date-selector-inner h-[50px] lg:h-16 w-full px-[18px] lg:px-6 py-2.5 lg:py-3 rounded-20px border-2 border-theme-gray-light flex items-center gap-2 cursor-text hover:cursor-pointer"
                onClick={() => handleClickContainer()}
                onFocus={() => handleClickContainer()}
                tabIndex={0}
            >
                <div className="flex flex-1" onClick={() => handleClickContainer()}>
                    <IconCalendar cssClasses="w-[22px]" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={!gridViewState && selectedDateRange.start ? formatDateRange(selectedDateRange) : selectedOptions}
                        placeholder={selectedOptions.length === 0 && !selectedDateRange.start && showPlaceholder ? placeholder : ''}
                        onBlur={() => handleInputBlur(selectedOptions)}
                        readOnly
                        className="w-full text-[16px] lg:text-[18px]
                        outline-none bg-transparent font-reckless
                        px-2
                        placeholder:text-theme-black cursor-pointer
                        overflow-hidden text-ellipsis whitespace-nowrap
                        "
                    />
                </div>
                {(selectedOptions.length !== 0 || selectedDateRange.start) && (
                    <button className="p-2 pr-0 cursor-pointer"
                        onClick={resetDates}>
                        <IconCloseBig cssClasses="w-2.5 lg:w-4 mt-0.5"/>
                    </button>
                )}
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="travel-date-selector-popover absolute top-full left-0 p-[14px] right-0 mt-2 bg-white rounded-10px shadow-filter-box overflow-y-auto z-50">
                    <SlideToggle
                        gridViewState={gridViewState}
                        setGridViewState={setGridViewState}
                        leftLabel="Monate"
                        rightLabel="Datum"
                        cssClass="w-full h-10 grid grid-cols-2"
                    ></SlideToggle>

                    {gridViewState ? (
                        <YearMonthPicker
                            isAlleSpaeterenTermineSelected={isAlleSpaeterenTermineSelected}
                            setIsAlleSpaeterenTermineSelected={setIsAlleSpaeterenTermineSelected}
                            isFlexibelSelected={isFlexibelSelected}
                            setIsFlexibelSelected={setIsFlexibelSelected}
                            options={options}
                            selectedValues={selectedValues}
                            onSelectionChange={handleYearMonthSelectionChange}
                            setIsOpen={setIsOpen}
                        />
                    ) : (
                        <DateRangePicker
                            setIsOpen={setIsOpen}
                            selectedDate={selectedDateRange}
                            onDateSelect={handleDateRangeSelect}
                        />
                    )}

                </div>
            )}

            <div className="travel-date-selector-label absolute h-[15px] lg:h-[18px] top-0 -mt-[15px] lg:-mt-[17px] left-7 inline-flex bg-white px-2 py-0 lg:p-2 lg:py-0 text-[10px] lg:text-[12px] tracking-[1px] translate-y-1/2">
                {label}
            </div>
        </div>
    )
}
